import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { WebSettings, WebSettingsPayload } from '../../../common/models'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getWebSettings,
  updateWebSettings,
} from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Button, Card, Textarea } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'

interface ICustomCodeValues {
  head?: string
  afterBodyOpen?: string
  beforeBodyClose?: string
}

export const CustomCodePage: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [customCode, setCustomCode] = useState<WebSettings>()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getWebSettings()

        setCustomCode(response.data)
        setValue('head', response.data.codeHead)
        setValue('afterBodyOpen', response.data.codeBodyStart)
        setValue('beforeBodyClose', response.data.codeBodyEnd)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schema = yup.object().shape({
    head: yup.string().required('Field is required'),
    afterBodyOpen: yup.string().required('Field is required'),
    beforeBodyClose: yup.string().required('Field is required'),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm<ICustomCodeValues>({
    mode: 'onChange',
    resolver: yupResolver<ICustomCodeValues>(schema),
    defaultValues: {
      head: '',
      afterBodyOpen: '',
      beforeBodyClose: '',
    },
  })
  const submit = async (values: ICustomCodeValues) => {
    const data: WebSettingsPayload = {
      codeHead: values.head,
      codeBodyStart: values.afterBodyOpen,
      codeBodyEnd: values.beforeBodyClose,
    }

    try {
      const { status } = await updateWebSettings(data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (customCode) {
      setValue('head', customCode.codeHead)
      setValue('afterBodyOpen', customCode.codeBodyStart)
      setValue('beforeBodyClose', customCode.codeBodyEnd)
    }
  }

  return (
    <>
      <Header breadcrumbs={['Settings', 'Custom Code']} />
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120 max-h-[calc(100vh-118px)] overflow-y-auto space-y-4">
        <form id="settings-custom-code-form" onSubmit={handleSubmit(submit)}>
          <h3 className="text-title-xs text-monochrome-1 mb-6">
            {'Custom Code </>'}
          </h3>
          <div className="flex flex-col items-start gap-3">
            <FormGroup label="Custom Header Code Before </head>">
              <Textarea
                isInvalid={!!errors?.head?.message}
                {...register('head')}
                placeholder="Enter your code here ..."
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Custom Code After <body>">
              <Textarea
                isInvalid={!!errors?.afterBodyOpen?.message}
                {...register('afterBodyOpen')}
                placeholder="Enter your code here ..."
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Custom Code Before </body>">
              <Textarea
                isInvalid={!!errors?.beforeBodyClose?.message}
                {...register('beforeBodyClose')}
                placeholder="Enter your code here ..."
                disabled={!hasEditPermission}
              />
            </FormGroup>
            <FormGroup label="Custom Code Before </body>">
              <Textarea
                isInvalid={!!errors?.beforeBodyClose?.message}
                {...register('beforeBodyClose')}
                placeholder="Enter your code here ..."
                disabled={!hasEditPermission}
              />
            </FormGroup>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 mt-4 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-custom-code-form"
            type="submit"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
